import { useState } from 'react';
import {
  apiErrorText,
  useLazySearchRateCandidatesQuery,
  useSetTenderItemCodeMutation,
} from '@shared/api';
import type { TenderItem } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { DecisionBtns, EditWrapper, Muted } from './styled';
import { codeSourceLabel } from './tenderLabels';

interface CodeCellProps {
  item: TenderItem;
  estimateId: string;
  canWrite: boolean;
  region: string | null;
  objectType: string | null;
}

/**
 * Шифр позиции с правкой на месте.
 *
 * Сметчик до этого не мог ни принять подсказку, ни вбить шифр сам: таблица была
 * только на чтение, а кнопка «Принять» у замечания меняла лишь пометку. Здесь
 * шифр реально применяется — сервер пересчитывает позицию и итоги.
 */
export function CodeCell({
  item,
  estimateId,
  canWrite,
  region,
  objectType,
}: CodeCellProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.manual_code ?? item.resolved_code ?? item.code ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [save, { isLoading }] = useSetTenderItemCodeMutation();
  const [search, candidates] = useLazySearchRateCandidatesQuery();

  const shown = item.manual_code ?? item.resolved_code ?? item.code;
  const sourceLabel = codeSourceLabel(item.code_source);

  async function apply(code: string | null) {
    setErr(null);
    try {
      const res = await save({ estimateId, itemId: item.id, code }).unwrap();
      // Шифр записан, но нормативом не подтверждён — показываем прямо, иначе
      // сметчик увидит «сохранено» и неизменившуюся сумму.
      setErr(res.warning ?? null);
      if (!res.warning) setEditing(false);
    } catch (e) {
      setErr(apiErrorText(e, 'Не удалось сохранить шифр'));
    }
  }

  if (!editing) {
    return (
      <div>
        {shown ?? '—'}
        {item.resolved_method && <Muted>{item.resolved_method}</Muted>}
        {sourceLabel && <Muted>{sourceLabel}</Muted>}
        {canWrite && (
          <Button $variant="ghost" $size="sm" type="button" onClick={() => setEditing(true)}>
            {shown ? 'Изменить' : 'Указать шифр'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <EditWrapper>
      <Input
        value={value}
        placeholder="ФЕР08-02-142-01"
        onChange={(e) => setValue(e.target.value)}
        list={`cand-${item.id}`}
        onFocus={() => {
          // Подсказки по наименованию работы — тот же поиск, что питает
          // автоподбор. Без него ручной ввод вслепую: справочник расценок в
          // интерфейсе больше нигде не доступен.
          void search({
            q: item.name,
            region: region ?? undefined,
            object_type: objectType ?? undefined,
            limit: 8,
          });
        }}
      />
      <datalist id={`cand-${item.id}`}>
        {(candidates.data?.candidates ?? []).map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </datalist>
      {candidates.isError && <Muted>Подсказки недоступны — введите шифр вручную</Muted>}
      <DecisionBtns>
        <Button
          $size="sm"
          type="button"
          disabled={isLoading}
          onClick={() => void apply(value.trim() || null)}
        >
          Сохранить
        </Button>
        {item.manual_code && (
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            disabled={isLoading}
            title="Вернуть шифр из тендера и автоподбор"
            onClick={() => void apply(null)}
          >
            Сбросить
          </Button>
        )}
        <Button
          $variant="ghost"
          $size="sm"
          type="button"
          disabled={isLoading}
          onClick={() => {
            setEditing(false);
            setErr(null);
            setValue(item.manual_code ?? item.resolved_code ?? item.code ?? '');
          }}
        >
          Отмена
        </Button>
      </DecisionBtns>
      {err && <Muted>{err}</Muted>}
    </EditWrapper>
  );
}
